// The Capital — a separate low-poly city the hero can "visit".
//
// Same trick as adventureScene: the cat stays in the same coordinate space; we hide
// the home objects, reveal the city group, drop the cat at its gate, and swap the
// hero's walking rules (heroController.setWalkRules) so it can roam the streets but
// not walk through buildings. The follow-camera keeps tracking the cat unchanged.
//
// Art is loaded LAZILY on the first visit (≈1.4 MB total) behind a travel screen with a
// real progress bar — it costs nothing for players who never go. The ~1,600 placed
// objects are only 134 unique meshes, so everything is drawn with InstancedMesh, split
// into spatial chunks so off-screen parts of the city are culled.

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { fixGltfScene } from './depthFix.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { Events, on, emit } from '../ui/eventBus.js'
import { CapitalConfig as CFG } from '../config/capital.js'
import * as advManager from '../game/adventureManager.js'
import { setWalkRules, snapCameraToHero, getViewAzimuth } from './heroController.js'
import { GROUND_Y } from './hexGrid.js'
import * as travel from '../ui/capitalTravel.js'
import { loadNpcAssets, createNpcs } from './capitalNpcs.js'
import { loadNav } from './capitalNav.js'
import { startTrip, endTrip } from '../game/capitalTrip.js'
import { setAway, isAwayBusy } from '../game/awayState.js'
import { createCapitalMap } from '../ui/capitalMap.js'
import { createCapitalLeads, loadTrails } from './capitalLeads.js'
import { isAdmitted } from '../game/capitalGuards.js'
import { updateCapitalDealsUI, hideCapitalDealsUI } from '../ui/capitalDealsUI.js'
import { createThief } from './capitalThief.js'
import { checkTrespass } from '../game/capitalGuards.js'

let sceneRef = null
let heroRef = null
let state = 'idle' // idle → travelling → active → leaving → idle
let built = null   // { root, materials, walk, spawn, npcs, nav }
let capMap = null  // minimap + fog of war (DOM, hidden until you're in the Capital)
let hidden = new Set()
let savedHeroPos = null
let savedHeroRot = 0

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
const announce = () => {
  const p = { active: state === 'active', busy: state === 'travelling' || state === 'leaving' }
  emit(Events.CapitalChange, p)
  setAway('capital', p) // (the RETURN HOME button and the locked home panels follow this)
}

export function createCapitalScene({ scene, hero }) {
  sceneRef = scene
  heroRef = hero
  capMap = createCapitalMap()
  on(Events.VisitCapital, () => { visit() })
  on(Events.LeaveCapital, () => { leave() })
  // An adventure takes over the world: quietly come home first.
  on(Events.AdventureStart, () => { if (state === 'active') leave({ instant: true }) })
  if (import.meta.env?.DEV) {
    window.__capital = {
      visit, leave, state: () => state, get map() { return capMap },
      // DEV: show palette colours with no lighting at all (separates "UV/palette wrong" from "lighting dim").
      unlit: () => { for (const m of built.materials) if (m) { m.emissiveMap = m.map; m.emissiveIntensity = 1; m.color.setScalar(0) } },
      // DEV: jump the hero to a source-metre spot (lowest reachable floor, or floors[i]) to inspect remote areas.
      thief: () => built?.thief,
      leads: () => built?.leads?.state() ?? null,
      teleport: (mx, mz, i = 0) => {
        const f = built.nav?.floorsAt(mx, mz) ?? []
        if (!f.length) return { ok: false }
        const r = built.root
        heroRef.position.set(r.position.x + mx * r.scale.x, r.position.y + f[Math.min(i, f.length - 1)] * r.scale.x, r.position.z + mz * r.scale.x)
        snapCameraToHero()
        return { ok: true, floors: f }
      },
      debug: () => {
        if (!built) return null
        const r = built.root
        const ims = r.children.filter((c) => c.isInstancedMesh)
        const box = new THREE.Box3().setFromObject(r)
        return {
          visible: r.visible, rootPos: r.position.toArray(), scale: r.scale.x, childCount: r.children.length,
          instanced: ims.length, totalInstances: ims.reduce((a, c) => a + c.count, 0),
          worldBox: [box.min.toArray(), box.max.toArray()],
          hero: heroRef.position.toArray(),
          npcs: built.npcs?.debug(),
          heights: (() => {
            heroRef.updateMatrixWorld(true); built.root.updateMatrixWorld(true)
            const h = new THREE.Box3().setFromObject(heroRef)
            const n = built.npcs?.heightWorld?.()
            return { catWorld: +(h.max.y - h.min.y).toFixed(3), warriorWorld: n && +n.toFixed(3), houseDoorWorld: +(2.3 * built.root.scale.x).toFixed(3) }
          })(),
          sample: ims.slice(0, 3).map((c) => ({ name: c.name, count: c.count, sphere: c.boundingSphere && [c.boundingSphere.center.toArray(), c.boundingSphere.radius], mat: c.material.type, hasMap: !!c.material.map })),
        }
      },
    }
  }
  return { visit, leave, isActive: () => state === 'active' }
}

export const isCapitalActive = () => state === 'active'

// Per-frame: hide anything that appeared at home while we're away (raiders, effects…),
// and make the lanterns/windows glow as night falls.
export function tickCapitalScene(night = 0, dt = 0) {
  if (state !== 'active' || !built || !sceneRef) return
  built.npcs?.tick(dt)
  {
    const k = built.root.scale.x
    const hx = (heroRef.position.x - built.root.position.x) / k
    const hz = (heroRef.position.z - built.root.position.z) / k
    const hy = (heroRef.position.y - built.root.position.y) / k
    updateCapitalDealsUI(hx, hz, capMap.pois())
    checkTrespass(hx, hz, capMap.restricted())
    built.thief?.tick(dt, hx, hz, hy)
    built.leads?.tick(dt, hx, hz)
    capMap.update({
      hx, hz,
      heading: heroRef.rotation.y,
      az: getViewAzimuth(),
      npcs: [...(built.npcs?.positions() ?? []), ...(built.thief?.positions() ?? [])],
    })
  }
  for (const child of sceneRef.children) {
    if (child === heroRef || child.isLight || child.userData?.isCapital || child.userData?.isBiome) continue
    if (child.visible) { hidden.add(child); child.visible = false }
  }
  const glow = Math.max(0, Math.min(1, night)) * 1.6
  for (const m of built.materials) if (m) m.emissiveIntensity = glow
}

// ── Trips ───────────────────────────────────────────────────────────────────

async function visit() {
  if (state !== 'idle' || !sceneRef) return
  if (advManager.isAdventuring?.()) return
  if (isAwayBusy()) return // already travelling to, or in, another place
  state = 'travelling'; announce()
  const t0 = performance.now()
  await travel.open({ title: 'Travelling to Capital City', cn: '京城' })
  try {
    if (!built) built = await build((p) => travel.setProgress(p))
  } catch (err) {
    console.error('[capital] could not build the city:', err)
    state = 'idle'; announce()
    await travel.fail()
    return
  }
  const wait = CFG.travelMinMs - (performance.now() - t0)
  if (wait > 0) await sleep(wait)
  swapIn()
  state = 'active'; announce()
  await travel.close()
}

async function leave({ instant = false } = {}) {
  if (state !== 'active') return
  state = 'leaving'; announce()
  if (!instant) await travel.open({ title: 'Returning home', cn: '归家', tip: false })
  const summary = endTrip() // settles the trip: applies capital gains/losses to the treasury
  swapOut()
  document.body.classList.remove('in-capital')
  capMap.setVisible(false)
  hideCapitalDealsUI()
  state = 'idle'; announce()
  if (!instant) { await sleep(300); await travel.close() }
  if (summary) emit(Events.CapitalReturn, { summary }) // the "welcome home" tally
}

function hideHome() {
  hidden = new Set()
  for (const child of sceneRef.children) {
    if (child === heroRef || child.isLight || child.userData?.isCapital || child.userData?.isBiome) continue
    if (child.visible) { hidden.add(child); child.visible = false }
  }
}
function restoreHome() {
  for (const o of hidden) if (o.parent) o.visible = true
  hidden = new Set()
}

function swapIn() {
  hideHome()
  built.root.visible = true
  savedHeroPos = heroRef.position.clone()
  savedHeroRot = heroRef.rotation.y
  heroRef.position.set(built.spawn.x, built.spawn.y, built.spawn.z)
  heroRef.rotation.y = Math.PI
  setWalkRules(built.walk)
  snapCameraToHero()
  document.body.classList.add('in-capital') // swaps the home HUD panels for the trip ledger
  capMap.setVisible(true)
  built.thief?.reset()
  startTrip('Capital City')
}

function swapOut() {
  built.root.visible = false
  setWalkRules(null)
  restoreHome()
  if (savedHeroPos) heroRef.position.copy(savedHeroPos)
  heroRef.rotation.y = savedHeroRot
  snapCameraToHero()
}

// ── Build (first visit only) ────────────────────────────────────────────────

function paletteTexture(url, srgb = true) {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(url, (t) => {
      t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
      t.flipY = false                       // glTF UV convention
      t.magFilter = THREE.NearestFilter     // crisp palette swatches
      t.minFilter = THREE.NearestFilter
      t.generateMipmaps = false
      resolve(t)
    }, undefined, reject)
  })
}

async function build(progress) {
  const base = CFG.dir
  let pGlb = 0, pLay = 0, pTex = 0
  const report = () => progress(0.62 * pGlb + 0.1 * pLay + 0.18 * pTex)

  const loader = new GLTFLoader()
  loader.setMeshoptDecoder(MeshoptDecoder)
  const glbP = loader.loadAsync(`${base}/${CFG.piecesFile}`, (e) => {
    if (e.lengthComputable) { pGlb = e.loaded / e.total; report() }
  }).then((g) => { fixGltfScene(g.scene); pGlb = 1; report(); return g })
  const layP = fetch(`${base}/${CFG.layoutFile}`).then((r) => {
    if (!r.ok) throw new Error(`layout ${r.status}`)
    return r.json()
  }).then((j) => { pLay = 1; report(); return j })

  const paletteIds = [1, 2, 3]
  let texDone = 0
  const texTick = (t) => { texDone += 1; pTex = texDone / (paletteIds.length + 1); report(); return t }
  const palP = Promise.all(paletteIds.map((n) =>
    paletteTexture(`${base}/PolygonSamurai_Tex_0${n}.png`).then(texTick)))
  const emiP = paletteTexture(`${base}/${CFG.emission}`).then(texTick)

  // Wandering townsfolk are a bonus: if their files are missing the city still works.
  const npcP = CFG.npcCount > 0
    ? loadNpcAssets(base).catch((err) => { console.warn('[capital] townsfolk unavailable:', err.message); return null })
    : Promise.resolve(null)

  // The baked walkable-floor map lets the hero use stairs, bridges and hills. If it's missing we fall back
  // to flat ground with simple building footprints.
  // Map picture + landmark list (non-fatal: without them there's simply no minimap).
  const mapP = Promise.all([
    new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = `${base}/capital_map.jpg` }),
    fetch(`${base}/capital_pois.json`).then((r) => { if (!r.ok) throw new Error(`pois ${r.status}`); return r.json() }),
  ]).catch((err) => { console.warn('[capital] no map:', err.message || err); return null })

  const navP = loadNav(`${base}/${CFG.navFile}`).catch((err) => { console.warn('[capital] no floor map, walking flat:', err.message); return null })

  const trailsP = loadTrails(base).catch((err) => { console.warn('[capital] no exploration leads:', err.message || err); return null })

  const [gltf, layout, palettes, emission, npcAssets, nav, mapAssets, trails] = await Promise.all([glbP, layP, palP, emiP, npcP, navP, mapP, trailsP])
  if (mapAssets) capMap.attach({ image: mapAssets[0], pois: mapAssets[1], nav })

  // One material per palette; all share the night-glow map (off by day, on at night).
  const materials = []
  paletteIds.forEach((n, i) => {
    materials[n] = new THREE.MeshLambertMaterial({
      map: palettes[i], emissive: 0xffffff, emissiveMap: emission, emissiveIntensity: 0, side: THREE.DoubleSide,
    })
    materials[n].color.setScalar(CFG.brightness)
  })

  const S = CFG.scale
  const root = new THREE.Group()
  root.userData.isCapital = true
  root.visible = false
  root.scale.setScalar(S)

  // Spawn = the street tile chosen at export time; place the city so it lands on the origin.
  const m = layout.meta
  root.position.set(-m.spawn.x * S, GROUND_Y - CFG.groundLevelM * S, -m.spawn.z * S)

  // Ground under everything so there are no see-through gaps between the art's tiles.
  const b = m.buildings
  const pad = 140
  const gw = (b.maxX - b.minX) + pad * 2
  const gd = (b.maxZ - b.minZ) + pad * 2
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(gw, gd),
    new THREE.MeshLambertMaterial({ color: 0x78904a }),
  )
  ground.rotation.x = -Math.PI / 2
  ground.position.set((b.minX + b.maxX) / 2, CFG.groundFloorM ?? -1.05, (b.minZ + b.maxZ) / 2)
  root.add(ground)

  // Instanced pieces, bucketed into spatial chunks for frustum culling.
  const nodeM = new THREE.Matrix4()
  const instM = new THREE.Matrix4()
  for (const piece of layout.pieces) {
    const node = gltf.scene.getObjectByName(piece.id)
    let mesh = null
    node?.traverse((o) => { if (!mesh && o.isMesh) mesh = o })
    if (!mesh) continue
    mesh.updateWorldMatrix(true, false)
    nodeM.copy(mesh.matrixWorld) // includes any quantisation scale baked in by the optimiser
    const buckets = new Map()
    for (let i = 0; i < piece.count; i++) {
      instM.fromArray(piece.m, i * 16)
      const key = `${Math.floor(piece.m[i * 16 + 12] / CFG.chunkM)},${Math.floor(piece.m[i * 16 + 14] / CFG.chunkM)}`
      let list = buckets.get(key)
      if (!list) buckets.set(key, (list = []))
      list.push(new THREE.Matrix4().multiplyMatrices(instM, nodeM))
    }
    const mat = materials[piece.tex] ?? materials[1]
    for (const list of buckets.values()) {
      const im = new THREE.InstancedMesh(mesh.geometry, mat, list.length)
      list.forEach((mm, i) => im.setMatrixAt(i, mm))
      im.instanceMatrix.needsUpdate = true
      im.computeBoundingSphere()
      im.name = piece.name
      root.add(im)
    }
  }
  sceneRef.add(root)

  // Collision: precomputed footprints (oriented rectangles) in a coarse spatial hash.
  const CELL = 4
  const grid = new Map()
  const cols = layout.colliders
  cols.forEach((c, idx) => {
    const [cx, cz, hx, hz, ax, az] = c
    const ex = Math.abs(ax) * hx + Math.abs(az) * hz
    const ez = Math.abs(az) * hx + Math.abs(ax) * hz
    for (let ix = Math.floor((cx - ex) / CELL); ix <= Math.floor((cx + ex) / CELL); ix++) {
      for (let iz = Math.floor((cz - ez) / CELL); iz <= Math.floor((cz + ez) / CELL); iz++) {
        const k = `${ix},${iz}`
        let l = grid.get(k)
        if (!l) grid.set(k, (l = []))
        l.push(idx)
      }
    }
  })
  const bounds = {
    minX: b.minX - CFG.walkMarginM, maxX: b.maxX + CFG.walkMarginM,
    minZ: b.minZ - CFG.walkMarginM, maxZ: b.maxZ + CFG.walkMarginM,
  }
  const padM = CFG.heroRadiusM
  const standableM = (mx, mz) => {
    if (mx < bounds.minX || mx > bounds.maxX || mz < bounds.minZ || mz > bounds.maxZ) return false
    const list = grid.get(`${Math.floor(mx / CELL)},${Math.floor(mz / CELL)}`)
    if (!list) return true
    for (const idx of list) {
      const [cx, cz, hx, hz, ax, az] = cols[idx]
      const dx = mx - cx
      const dz = mz - cz
      if (Math.abs(dx * ax + dz * az) <= hx + padM && Math.abs(-dx * az + dz * ax) <= hz + padM) return false
    }
    return true
  }
  // Walking rules, in two flavours (both work in SOURCE METRES: (x, z, currentFloorHeight) → floor height | null):
  //  • floor map  — height-aware; the hero follows stairs, bridges and hills
  //  • flat       — fallback: the old building footprints on level ground
  const flat = (mx, mz) => (standableM(mx, mz) ? CFG.groundLevelM : null)
  const heroStepM = nav ? (mx, mz, my) => nav.step(mx, mz, my, CFG.heroRadiusM, CFG.heroClearM) : flat
  const npcStepM = nav ? (mx, mz, my) => nav.step(mx, mz, my, CFG.heroRadiusM, CFG.npcClearM) : flat
  // World (x,z,y) → source metres → rule → back to a world height.
  const walk = (wx, wz, wy) => {
    const h = heroStepM((wx - root.position.x) / S, (wz - root.position.z) / S, (wy - root.position.y) / S)
    return h === null ? null : root.position.y + h * S
  }

  // Spawn: the world origin (the chosen street tile), nudged to the nearest spot the hero can stand.
  let spawn = { x: 0, z: 0, y: GROUND_Y }
  const startY = (x, z) => walk(x, z, GROUND_Y)
  if (startY(0, 0) === null) {
    search: for (let r = 0.1; r < 4; r += 0.1) {
      for (let a = 0; a < Math.PI * 2; a += Math.PI / 8) {
        const x = Math.cos(a) * r, z = Math.sin(a) * r
        if (startY(x, z) !== null) { spawn = { x, z, y: startY(x, z) }; break search }
      }
    }
  } else spawn.y = startY(0, 0)

  const npcs = npcAssets
    ? createNpcs({ parent: root, assets: npcAssets, stepM: npcStepM, centre: { x: m.spawn.x, z: m.spawn.z, y: CFG.groundLevelM } })
    : null
  const thief = npcAssets ? createThief({ parent: root, assets: npcAssets, stepM: npcStepM }) : null
  // Faint rings / stepping stones toward landmarks you haven't found yet (see docs/CAPITAL-LEADS.md).
  const leads = trails && mapAssets
    ? createCapitalLeads({ root, trails, pois: mapAssets[1].pois, isFound: (id) => capMap.isDiscovered(id), isAdmitted })
    : null
  progress(1)
  return { root, materials, walk, spawn, npcs, thief, nav, leads }
}
